import { usePreferencesStore } from '@/entities/preferencesStore';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { useNewStrategyWizardStore } from '@/features/Strategies/stores/strategyWizardStore';
import { useSecuritySelectionStore } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/SecuritySelection/store/useSecuritySelectionStore';
import { useStartingUniverseStore } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/store/useStartingUniverseStore';
import { getContext } from '@/shared/integrations/tanstack-query/root-provider';

/**
 * Persist storage keys for each store. Must stay in sync with the `name`
 * field in each store's `persist()` middleware configuration.
 */
const SESSION_STORAGE_KEYS = [
  'smart-beta-strategy-storage',
  'new-strategy-wizard-store',
  'report-data-store',
  'performance-page-store',
] as const;

// 'biasafe-strategies' used to back the localStorage-persisted mock
// strategies store; the real /strategies API replaced it. Listed here
// to wipe any stale data still sitting in browsers that logged in
// during the mock era.
//
// 'biasafe.notifications.read' is the header bell's read-state (see
// notificationsRepository). The feed is org-wide and the ids are not per user, so
// a colleague signing in on the same machine inherited the previous person's
// "read" marks and never saw those notifications as unread.
const LOCAL_STORAGE_KEYS = [
  'biasafe-strategies',
  'app-preferences',
  'biasafe.notifications.read',
] as const;

/**
 * Purges all Zustand stores and the React Query cache on logout to prevent
 * data leakage between user sessions.
 *
 * This function must NOT import useUserStore — it is called from within
 * useUserStore.logout() and importing it would create a circular dependency.
 * useUserStore resets its own state internally.
 */
/**
 * Run one cleanup step, reporting rather than propagating a failure.
 *
 * Every step below is independent, so one broken store must not stop the
 * others — in particular it must not stop the React Query cache from being
 * cleared, which is the step that actually prevents one user's server data
 * from being visible to the next.
 */
function step(label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error(`[auth] logout cleanup step "${label}" failed`, err);
  }
}

export function resetAllStores(): void {
  // 1. Clear persisted storage so stale data is never rehydrated on next login
  step('persisted storage', () => {
    SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  });

  // 2. Reset in-memory state for stores with explicit reset actions
  step('strategy definition form', () =>
    useSmartBetaStrategyStore.getState().reset(),
  );
  step('strategy wizard', () =>
    useNewStrategyWizardStore.getState().resetWizard(null),
  );
  step('security selection', () => {
    useSecuritySelectionStore.getState().resetDraft();
    useSecuritySelectionStore.getState().resetStepEditor();
  });

  // 3. Reset stores without reset actions via setState merge.
  //    Merge mode preserves the action functions — replace mode would wipe them.
  step('starting universe', () =>
    useStartingUniverseStore.setState({
      expandedEligibilityRuleIds: {},
      expandedFilterSectionIds: {},
    }),
  );
  // APPEARANCE IS CLEARED ON SIGN-OUT, and it is worth saying why, because an
  // earlier version of this block argued the exact opposite in writing.
  //
  // It used to survive, on the reasoning that a dark-mode choice describes the
  // DEVICE and says nothing about the account that made it. That was true
  // while the only copy lived in this browser. It stopped being true when
  // appearance moved onto the account: the localStorage copy is now a CACHE of
  // a specific user's setting, and a cache that outlives the session is how
  // two people sharing a machine end up wearing each other's theme.
  //
  // So it resets, and the cost is one frame of default appearance right after
  // a fresh sign-in, before `GET /user` resolves and `useAccountAppearance`
  // hydrates from the account. That is the cheaper of the two wrongs, and it
  // is the trade the per-account behaviour was chosen with eyes open.
  step('preferences', () =>
    usePreferencesStore.setState({
      isStrategiesBannerVisible: true,
      isCollapsed: false,
      viewMode: 'grid',
      theme: 'system',
      cornerStyle: 'square',
    }),
  );

  // 4. Clear the React Query cache to prevent user-specific server data leakage
  step('query cache', () => getContext().queryClient.clear());
}
